import React, { useState, useEffect, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ArrowLeft, RotateCcw, Save, Undo2, GripVertical, Eye, EyeOff, ChevronDown, ChevronRight, Monitor, PanelLeft } from "lucide-react";
import Icon from "../../ui/icons";
import CardBasic from "../../ui/Card.jsx";
import ConfirmModal from "../../shared/ConfirmModal.jsx";
import { showMessage } from "../../../scripts/showMessage.js";
import {
  DEFAULT_VERTICAL_MENU,
  DEFAULT_HORIZONTAL_MENU,
} from "./menuConstants";

// Styles
const actionBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.5rem',
  background: 'var(--bg-secondary, #f5f7f9)',
  borderBottom: '1px solid var(--border-light, #e2e8f0)'
};

const backButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 1rem',
  border: '1px solid var(--border-medium, #cbd5e1)',
  borderRadius: '8px',
  background: 'var(--bg-card, #ffffff)',
  color: 'var(--text-primary, #2c3e50)',
  fontSize: '0.875rem',
  fontWeight: '500',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};

const saveButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 1.25rem',
  border: 'none',
  borderRadius: '8px',
  background: 'var(--accent-slate, #5b7c8d)',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};

const containerStyle = {
  display: 'flex',
  minHeight: '500px'
};

const sidebarStyle = {
  width: '200px',
  flexShrink: 0,
  borderRight: '1px solid var(--border-light, #e2e8f0)',
  background: 'var(--bg-secondary, #f5f7f9)'
};

const sidebarNavStyle = {
  padding: '1rem 0'
};

const sidebarItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.75rem 1.25rem',
  fontSize: '0.875rem',
  color: 'var(--text-secondary, #64748b)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderLeft: '3px solid transparent'
};

const sidebarItemActiveStyle = {
  ...sidebarItemStyle,
  background: 'var(--bg-card, #ffffff)',
  color: 'var(--accent-slate, #5b7c8d)',
  borderLeftColor: 'var(--accent-slate, #5b7c8d)',
  fontWeight: '500'
};

const contentStyle = {
  flex: 1,
  padding: '1.5rem',
  overflowY: 'auto'
};

const loadingStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  gap: '1rem'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1.5rem'
};

const titleStyle = {
  fontSize: '1.125rem',
  fontWeight: '600',
  color: 'var(--text-primary, #2c3e50)',
  margin: 0
};

const toolbarStyle = {
  display: 'flex',
  gap: '0.5rem'
};

const toolbarButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
  padding: '0.375rem 0.75rem',
  border: '1px solid var(--border-medium, #cbd5e1)',
  borderRadius: '6px',
  background: 'var(--bg-card, #ffffff)',
  color: 'var(--text-secondary, #64748b)',
  fontSize: '0.8125rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};

const listContainerStyle = {
  background: 'var(--bg-card, #ffffff)',
  border: '1px solid var(--border-light, #e2e8f0)',
  borderRadius: '12px',
  overflow: 'hidden'
};

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.625rem 1rem',
  borderBottom: '1px solid var(--border-light, #e2e8f0)',
  background: 'var(--bg-card, #ffffff)',
  transition: 'background 0.15s ease'
};

const itemHiddenStyle = {
  ...itemStyle,
  opacity: 0.5,
  background: 'var(--bg-secondary, #f5f7f9)'
};

const childItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.5rem 1rem 0.5rem 3rem',
  borderBottom: '1px solid var(--border-light, #e2e8f0)',
  background: 'var(--bg-card, #ffffff)',
  fontSize: '0.8125rem'
};

const childItemHiddenStyle = {
  ...childItemStyle,
  opacity: 0.5,
  background: 'var(--bg-secondary, #f5f7f9)'
};

const dragHandleStyle = {
  display: 'flex',
  alignItems: 'center',
  color: 'var(--text-muted, #94a3b8)',
  cursor: 'grab'
};

const visibilityButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  border: 'none',
  borderRadius: '6px',
  background: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};

const expandButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  border: 'none',
  borderRadius: '4px',
  background: 'transparent',
  color: 'var(--text-muted, #94a3b8)',
  cursor: 'pointer'
};

const infoBoxStyle = {
  padding: '1rem 1.5rem',
  background: 'var(--bg-secondary, #f5f7f9)',
  borderTop: '1px solid var(--border-light, #e2e8f0)',
  fontSize: '0.8125rem',
  color: 'var(--text-secondary, #64748b)',
  lineHeight: '1.6'
};

const mobileTabBarStyle = {
  display: 'flex',
  overflowX: 'auto',
  borderBottom: '1px solid var(--border-light, #e2e8f0)',
  background: 'var(--bg-secondary, #f5f7f9)',
  padding: '0 1rem'
};

const mobileTabStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.875rem 1rem',
  fontSize: '0.8125rem',
  fontWeight: '500',
  color: 'var(--text-secondary, #64748b)',
  whiteSpace: 'nowrap',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};

const mobileTabActiveStyle = {
  ...mobileTabStyle,
  color: 'var(--accent-slate, #5b7c8d)',
  borderBottomColor: 'var(--accent-slate, #5b7c8d)'
};

const MENU_TYPES = [
  { id: 'vertical', label: 'Vertikal', icon: PanelLeft, description: 'Seitenleiste links' },
  { id: 'horizontal', label: 'Horizontal', icon: Monitor, description: 'Leiste oben' }
];

const MenuEditor = () => {
  const [menuType, setMenuType] = useState("vertical");
  const [verticalMenuItems, setVerticalMenuItems] = useState(DEFAULT_VERTICAL_MENU);
  const [horizontalMenuItems, setHorizontalMenuItems] = useState(DEFAULT_HORIZONTAL_MENU);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [history, setHistory] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  // Responsive layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load menu configuration
  useEffect(() => {
    const fetchMenuConfig = async () => {
      try {
        const response = await fetch("/index.php/settings/get_menu_config");
        if (!response.ok) throw new Error("Failed to fetch");
        const data = await response.json();

        const mergeMenuItems = (apiItems, defaultItems) => {
          const apiItemsMap = new Map(apiItems.map((item) => [item.id, item]));
          return defaultItems.map((defaultItem) => {
            const apiItem = apiItemsMap.get(defaultItem.id);
            if (!apiItem) return { ...defaultItem };
            const merged = { ...defaultItem, ...apiItem };
            if (defaultItem.children) {
              const apiChildrenMap = new Map((apiItem.children || []).map((child) => [child.id, child]));
              merged.children = defaultItem.children.map((defaultChild) => {
                const apiChild = apiChildrenMap.get(defaultChild.id);
                return apiChild ? { ...defaultChild, ...apiChild } : { ...defaultChild };
              });
            }
            return merged;
          });
        };

        if (data.vertical?.items) {
          setVerticalMenuItems(mergeMenuItems(data.vertical.items, DEFAULT_VERTICAL_MENU));
        }
        if (data.horizontal?.items) {
          setHorizontalMenuItems(mergeMenuItems(data.horizontal.items, DEFAULT_HORIZONTAL_MENU));
        }
        if (data.activeMenuType) {
          setMenuType(data.activeMenuType);
        }

        // Expand all groups by default
        const allGroups = {};
        [...DEFAULT_VERTICAL_MENU, ...DEFAULT_HORIZONTAL_MENU].forEach(item => {
          if (item.children) allGroups[item.id] = true;
        });
        setExpandedGroups(allGroups);
      } catch (error) {
        console.error("Error fetching menu config:", error);
        showMessage("Fehler beim Laden der Menü-Konfiguration", "error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchMenuConfig();
  }, []);

  const currentMenuItems = menuType === "vertical" ? verticalMenuItems : horizontalMenuItems;
  const setCurrentMenuItems = menuType === "vertical" ? setVerticalMenuItems : setHorizontalMenuItems;

  // Save state to history before changes
  const saveToHistory = useCallback(() => {
    setHistory(prev => [...prev.slice(-9), {
      vertical: JSON.parse(JSON.stringify(verticalMenuItems)),
      horizontal: JSON.parse(JSON.stringify(horizontalMenuItems))
    }]);
  }, [verticalMenuItems, horizontalMenuItems]);

  // Undo last change
  const handleUndo = () => {
    if (history.length === 0) return;
    const lastState = history[history.length - 1];
    setVerticalMenuItems(lastState.vertical);
    setHorizontalMenuItems(lastState.horizontal);
    setHistory(prev => prev.slice(0, -1));
    setHasChanges(true);
  };

  // Reset to default
  const resetToDefault = () => {
    setShowResetModal(true);
  };

  const confirmReset = () => {
    setShowResetModal(false);
    saveToHistory();
    if (menuType === "vertical") {
      setVerticalMenuItems(DEFAULT_VERTICAL_MENU);
    } else {
      setHorizontalMenuItems(DEFAULT_HORIZONTAL_MENU);
    }
    setHasChanges(true);
    showMessage("Menü wurde zurückgesetzt", "success");
  };

  // Toggle visibility
  const toggleVisibility = (itemId, childId = null) => {
    saveToHistory();
    setCurrentMenuItems((prev) => {
      let newItems = prev.map((item) => {
        if (item.id === itemId) {
          if (item.locked) return item;
          if (childId && item.children) {
            return {
              ...item,
              children: item.children.map((child) =>
                child.id === childId ? { ...child, visible: !child.visible } : child
              ),
            };
          }
          return { ...item, visible: !item.visible };
        }
        return item;
      });

      // Mutual exclusion: top-level mailbox vs mailbox child in schriftliches
      if (itemId === 'mailbox' && !childId) {
        // Toggling top-level mailbox — if now visible, hide mailbox child in schriftliches
        const topMailbox = newItems.find(i => i.id === 'mailbox' && !i.children);
        if (topMailbox?.visible) {
          newItems = newItems.map(item => {
            if (item.id === 'schriftliches' && item.children) {
              return {
                ...item,
                children: item.children.map(child =>
                  child.id === 'mailbox' ? { ...child, visible: false } : child
                ),
              };
            }
            return item;
          });
        }
      } else if (itemId === 'schriftliches' && childId === 'mailbox') {
        // Toggling mailbox child in schriftliches — if now visible, hide top-level mailbox
        const schriftliches = newItems.find(i => i.id === 'schriftliches');
        const mailboxChild = schriftliches?.children?.find(c => c.id === 'mailbox');
        if (mailboxChild?.visible) {
          newItems = newItems.map(item =>
            item.id === 'mailbox' && !item.children ? { ...item, visible: false } : item
          );
        }
      }

      return newItems;
    });
    setHasChanges(true);
  };

  // Toggle group expand/collapse
  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Drag & Drop handler
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    // Prevent dragging locked items or moving items into locked positions
    const draggedItem = currentMenuItems[sourceIndex];
    if (draggedItem.locked) return;

    // Prevent moving items before the first locked item or after the last locked item
    const firstLockedIndex = currentMenuItems.findIndex(item => item.locked);
    const lastLockedIndex = currentMenuItems.length - 1 - [...currentMenuItems].reverse().findIndex(item => item.locked);
    if (destIndex <= firstLockedIndex || destIndex >= lastLockedIndex) return;

    saveToHistory();
    const newItems = Array.from(currentMenuItems);
    const [removed] = newItems.splice(sourceIndex, 1);
    newItems.splice(destIndex, 0, removed);
    setCurrentMenuItems(newItems.map((item, idx) => ({ ...item, order: idx })));
    setHasChanges(true);
  };

  // Save
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const menuConfig = {
        version: "1.0",
        activeMenuType: menuType,
        vertical: {
          items: verticalMenuItems.map((item) => {
            const apiItem = { id: item.id, visible: item.visible, order: item.order };
            if (item.locked) apiItem.locked = true;
            if (item.children) {
              apiItem.children = item.children.map((child) => ({
                id: child.id,
                visible: child.visible,
              }));
            }
            return apiItem;
          }),
        },
        horizontal: {
          items: horizontalMenuItems.map((item) => {
            const apiItem = { id: item.id, visible: item.visible, order: item.order };
            if (item.locked) apiItem.locked = true;
            if (item.children) {
              apiItem.children = item.children.map((child) => ({
                id: child.id,
                visible: child.visible,
              }));
            }
            return apiItem;
          }),
        },
      };

      const formData = new FormData();
      formData.append("menu_config", JSON.stringify(menuConfig));

      const response = await fetch("/index.php/settings/menu_config_update", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to save");
      const result = await response.json();

      if (result.success) {
        showMessage("Menü erfolgreich gespeichert", "success");
        setHasChanges(false);
        setHistory([]);
        // Dispatch event to notify menu components to refresh
        window.dispatchEvent(new CustomEvent('menuUpdated'));
      } else {
        showMessage("Fehler beim Speichern", "error");
      }
    } catch (error) {
      console.error("Error saving menu:", error);
      showMessage("Fehler beim Speichern des Menüs", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Render menu item
  const renderMenuItem = (item, index, provided, snapshot) => {
    const isExpanded = expandedGroups[item.id];
    const isDragging = snapshot?.isDragging;

    return (
      <div
        ref={provided?.innerRef}
        {...(provided?.draggableProps || {})}
        style={{
          ...provided?.draggableProps?.style,
          ...(isDragging ? { boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '8px' } : {})
        }}
      >
        <div style={item.visible ? itemStyle : itemHiddenStyle}>
          {/* Drag Handle */}
          <div {...(provided?.dragHandleProps || {})} style={{
            ...dragHandleStyle,
            ...(item.locked ? { cursor: 'not-allowed', opacity: 0.3 } : {})
          }}>
            <GripVertical size={18} />
          </div>

          {/* Visibility Toggle */}
          {item.locked ? (
            <div style={{ ...visibilityButtonStyle, cursor: 'not-allowed' }} title="Gesperrt">
              <Eye size={18} style={{ color: 'var(--text-muted, #94a3b8)', opacity: 0.4 }} />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => toggleVisibility(item.id)}
              style={visibilityButtonStyle}
              title={item.visible ? "Ausblenden" : "Einblenden"}
            >
              {item.visible ? (
                <Eye size={18} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
              ) : (
                <EyeOff size={18} style={{ color: 'var(--text-muted, #94a3b8)' }} />
              )}
            </button>
          )}

          {/* Icon */}
          <Icon
            name={item.icon}
            size={18}
            style={{ color: item.visible ? 'var(--text-secondary, #64748b)' : 'var(--text-muted, #94a3b8)' }}
          />

          {/* Name */}
          <span style={{
            flex: 1,
            fontSize: '0.875rem',
            fontWeight: '500',
            color: item.visible ? 'var(--text-primary, #2c3e50)' : 'var(--text-muted, #94a3b8)'
          }}>
            {item.name}
          </span>

          {/* Expand Button */}
          {item.children && (
            <button type="button" onClick={() => toggleGroup(item.id)} style={expandButtonStyle}>
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
          )}
        </div>

        {/* Children */}
        {item.children && isExpanded && (
          <div>
            {item.children.map((child) => (
              <div key={child.id} style={child.visible ? childItemStyle : childItemHiddenStyle}>
                <button
                  type="button"
                  onClick={() => toggleVisibility(item.id, child.id)}
                  style={visibilityButtonStyle}
                >
                  {child.visible ? (
                    <Eye size={16} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                  ) : (
                    <EyeOff size={16} style={{ color: 'var(--text-muted, #94a3b8)' }} />
                  )}
                </button>
                <span style={{
                  color: child.visible ? 'var(--text-primary, #2c3e50)' : 'var(--text-muted, #94a3b8)'
                }}>
                  {child.name}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render sidebar
  const renderSidebar = () => (
    <nav style={sidebarNavStyle}>
      {MENU_TYPES.map(type => {
        const IconComponent = type.icon;
        const isActive = menuType === type.id;
        return (
          <div
            key={type.id}
            style={isActive ? sidebarItemActiveStyle : sidebarItemStyle}
            onClick={() => setMenuType(type.id)}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'rgba(91, 124, 141, 0.05)';
                e.currentTarget.style.color = 'var(--text-primary, #2c3e50)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-secondary, #64748b)';
              }
            }}
          >
            <IconComponent size={18} />
            <div>
              <div style={{ fontWeight: isActive ? '500' : '400' }}>{type.label}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{type.description}</div>
            </div>
          </div>
        );
      })}
    </nav>
  );

  if (isLoading) {
    return (
      <CardBasic>
        <div style={loadingStyle}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p style={{ color: 'var(--text-secondary, #64748b)', margin: 0 }}>
            Lade Menü-Konfiguration...
          </p>
        </div>
      </CardBasic>
    );
  }

  return (
    <CardBasic style={{ padding: 0, overflow: 'hidden' }}>
      <form onSubmit={handleSubmit}>
        {/* Action Bar */}
        <div style={actionBarStyle}>
          <a href="index.php/settings" style={backButtonStyle}>
            <ArrowLeft size={18} />
            Zurück
          </a>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="submit"
              style={{
                ...saveButtonStyle,
                opacity: isSaving ? 0.7 : 1,
                cursor: isSaving ? 'not-allowed' : 'pointer'
              }}
              disabled={isSaving}
            >
              <Save size={18} />
              {isSaving ? 'Speichert...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Mobile Tab Bar */}
        {isMobile && (
          <div style={mobileTabBarStyle}>
            {MENU_TYPES.map(type => {
              const IconComponent = type.icon;
              return (
                <div
                  key={type.id}
                  style={menuType === type.id ? mobileTabActiveStyle : mobileTabStyle}
                  onClick={() => setMenuType(type.id)}
                >
                  <IconComponent size={16} />
                  {type.label}
                </div>
              );
            })}
          </div>
        )}

        {/* Main Content */}
        <div style={isMobile ? { padding: '1.5rem' } : containerStyle}>
          {/* Desktop Sidebar */}
          {!isMobile && (
            <div style={sidebarStyle}>
              {renderSidebar()}
            </div>
          )}

          {/* Content Area */}
          <div style={isMobile ? {} : contentStyle}>
            {/* Header with toolbar */}
            <div style={headerStyle}>
              <h3 style={titleStyle}>
                {menuType === 'vertical' ? 'Vertikales Menü' : 'Horizontales Menü'}
              </h3>
              <div style={toolbarStyle}>
                <button
                  type="button"
                  onClick={handleUndo}
                  disabled={history.length === 0}
                  style={{
                    ...toolbarButtonStyle,
                    opacity: history.length === 0 ? 0.5 : 1,
                    cursor: history.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                  title="Rückgängig"
                >
                  <Undo2 size={16} />
                </button>
                <button
                  type="button"
                  onClick={resetToDefault}
                  style={toolbarButtonStyle}
                  title="Auf Standard zurücksetzen"
                >
                  <RotateCcw size={16} />
                  Zurücksetzen
                </button>
              </div>
            </div>

            {/* Menu Items List */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="menu-items">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={listContainerStyle}
                  >
                    {currentMenuItems.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                        isDragDisabled={item.locked}
                      >
                        {(provided, snapshot) => renderMenuItem(item, index, provided, snapshot)}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>

        {/* Info Box */}
        <div style={infoBoxStyle}>
          <p style={{ marginBottom: '0.5rem' }}>
            <strong>Tipp:</strong> Ziehen Sie Menüpunkte per Drag-and-Drop, um die Reihenfolge zu ändern.
          </p>
          <p style={{ marginBottom: 0 }}>
            Untermenüs mit nur einem sichtbaren Element werden direkt (ohne Dropdown) angezeigt.
          </p>
        </div>
      </form>

      <ConfirmModal
        show={showResetModal}
        title="Menü zurücksetzen"
        message="Möchten Sie wirklich alle Änderungen verwerfen?"
        confirmText="Zurücksetzen"
        cancelText="Abbrechen"
        variant="warning"
        onConfirm={confirmReset}
        onCancel={() => setShowResetModal(false)}
      />
    </CardBasic>
  );
};

export default MenuEditor;
